"use client";

import React, { useState } from "react";
import { Text, SectionCard, CardTitle, Section, Badge } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import DashboardGrid, { DashboardGridItem } from "@/src/components/charts/dashboard-grid";
import { BarChart } from "@/src/components/charts/bar-chart";
import { DonutChart } from "@/src/components/charts/donut-chart";
import { AreaChart } from "@/src/components/charts/area-chart";
import { GaugeChart } from "@/src/components/charts/gauge-chart";

const barData = {
  categories: ["Q1", "Q2", "Q3", "Q4"],
  series: [{ name: "Revenue", data: [4200, 5100, 4800, 6200] }],
};

const donutData = {
  items: [
    { name: "Engineering", value: 45 },
    { name: "Sales", value: 30 },
    { name: "Marketing", value: 25 },
  ],
};

const areaData = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May"],
  series: [{ name: "Users", data: [120, 180, 250, 310, 420] }],
};

export default function DocsDashboardGridPage() {
  const [animationKey, setAnimationKey] = useState(0);

  const handleReplayAnimation = () => {
    setAnimationKey((prev) => prev + 1);
  };

  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Dashboard Grid
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              A responsive CSS Grid layout wrapper for arranging charts and KPI cards in
              dashboard layouts. Provides flexible column configurations, gap sizing, and
              optional staggered animations using design tokens.
            </Text>
          </div>
        </Section>

        {/* Basic 4-Column Grid */}
        <Section title="Basic 4-Column Grid">
          <SectionCard
            header={<CardTitle>Default 4-Column Layout</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DashboardGrid>
              <div className="bg-info-bg rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Card 1</Text>
              </div>
              <div className="bg-success-bg rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Card 2</Text>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Card 3</Text>
              </div>
              <div className="bg-warning-bg rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Card 4</Text>
              </div>
            </DashboardGrid>
          </SectionCard>
        </Section>

        {/* 3-Column with Spanning */}
        <Section title="3-Column with Spanning">
          <SectionCard
            header={<CardTitle>Responsive Column Spanning</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DashboardGrid columns={3}>
              <DashboardGridItem span={2}>
                <div className="bg-info-bg rounded-lg p-theme-4 flex items-center justify-center min-h-40">
                  <div className="text-center">
                    <Text className="text-sm font-semibold">Featured Card (Span 2)</Text>
                    <Text className="text-xs text-muted-foreground mt-1">
                      Takes 2 columns at lg breakpoint
                    </Text>
                  </div>
                </div>
              </DashboardGridItem>
              <DashboardGridItem>
                <div className="bg-success-bg rounded-lg p-theme-4 flex items-center justify-center min-h-40">
                  <div className="text-center">
                    <Text className="text-sm font-semibold">Side Card</Text>
                    <Text className="text-xs text-muted-foreground mt-1">
                      1 column width
                    </Text>
                  </div>
                </div>
              </DashboardGridItem>
              <DashboardGridItem>
                <div className="bg-purple-100 dark:bg-purple-900 rounded-lg p-theme-4 flex items-center justify-center min-h-40">
                  <div className="text-center">
                    <Text className="text-sm font-semibold">KPI Card</Text>
                    <Text className="text-xs text-muted-foreground mt-1">
                      Standard width
                    </Text>
                  </div>
                </div>
              </DashboardGridItem>
              <DashboardGridItem>
                <div className="bg-warning-bg rounded-lg p-theme-4 flex items-center justify-center min-h-40">
                  <div className="text-center">
                    <Text className="text-sm font-semibold">Metric</Text>
                    <Text className="text-xs text-muted-foreground mt-1">
                      1 column width
                    </Text>
                  </div>
                </div>
              </DashboardGridItem>
            </DashboardGrid>
          </SectionCard>
        </Section>

        {/* 2-Column Layout with Charts */}
        <Section title="2-Column Layout">
          <SectionCard
            header={<CardTitle>Two Equal-Width Chart Cards</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DashboardGrid columns={2}>
              <DashboardGridItem>
                <div className="bg-muted rounded-lg p-theme-4">
                  <Text className="text-xs font-semibold text-muted-foreground mb-theme-3">
                    Revenue Trend
                  </Text>
                  <BarChart data={barData} height={220} />
                </div>
              </DashboardGridItem>
              <DashboardGridItem>
                <div className="bg-muted rounded-lg p-theme-4">
                  <Text className="text-xs font-semibold text-muted-foreground mb-theme-3">
                    Team Distribution
                  </Text>
                  <DonutChart data={donutData} height={220} />
                </div>
              </DashboardGridItem>
            </DashboardGrid>
          </SectionCard>
        </Section>

        {/* Animated Grid with Replay Button */}
        <Section title="Animated Grid">
          <SectionCard
            header={
              <div className="flex items-center justify-between">
                <CardTitle>Staggered Fade-In Animation</CardTitle>
                <button
                  onClick={handleReplayAnimation}
                  className="px-theme-3 py-theme-2 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                >
                  Replay
                </button>
              </div>
            }
            contentClassName="p-theme-5"
          >
            <DashboardGrid
              key={animationKey}
              columns={3}
              animated
            >
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 1</Text>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 2</Text>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 3</Text>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900 dark:to-orange-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 4</Text>
              </div>
              <div className="bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900 dark:to-pink-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 5</Text>
              </div>
              <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900 dark:to-indigo-800 rounded-lg p-theme-4 flex items-center justify-center min-h-32">
                <Text className="text-center text-sm font-semibold">Item 6</Text>
              </div>
            </DashboardGrid>
            <Text className="text-xs text-muted-foreground mt-theme-4">
              The <code className="bg-muted px-1.5 py-0.5 rounded text-xs">animated</code> prop
              applies a staggered fade-in effect using CSS animations. Use the Replay button to
              re-trigger the animation.
            </Text>
          </SectionCard>
        </Section>

        {/* Gap Variants */}
        <Section title="Gap Variants">
          <div className="space-y-theme-4">
            <SectionCard
              header={<CardTitle>Small Gap (sm)</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DashboardGrid columns={4} gap="sm">
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
              </DashboardGrid>
            </SectionCard>

            <SectionCard
              header={<CardTitle>Medium Gap (md)</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DashboardGrid columns={4} gap="md">
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
              </DashboardGrid>
            </SectionCard>

            <SectionCard
              header={<CardTitle>Large Gap (lg)</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DashboardGrid columns={4} gap="lg">
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
              </DashboardGrid>
            </SectionCard>

            <SectionCard
              header={<CardTitle>Extra Large Gap (xl)</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DashboardGrid columns={4} gap="xl">
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
                <div className="bg-muted rounded p-theme-3 flex items-center justify-center h-20">
                  <Text className="text-xs font-semibold">Item</Text>
                </div>
              </DashboardGrid>
            </SectionCard>
          </div>
        </Section>

        {/* Props Table - DashboardGridProps */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>DashboardGridProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">columns</td>
                  <td className="py-2 px-2">1 | 2 | 3 | 4 | 6</td>
                  <td className="py-2 px-2">4</td>
                  <td className="py-2 px-2">Number of columns at desktop breakpoint</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">gap</td>
                  <td className="py-2 px-2">"sm" | "md" | "lg" | "xl"</td>
                  <td className="py-2 px-2">"md"</td>
                  <td className="py-2 px-2">Gap size between grid items</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">animated</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Enable staggered fade-in animation for children</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes to merge with defaults</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Props Table - DashboardGridItemProps */}
        <Section title="Item Props">
          <SectionCard
            header={<CardTitle>DashboardGridItemProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">span</td>
                  <td className="py-2 px-2">1 | 2 | 3 | 4 | 6</td>
                  <td className="py-2 px-2">1</td>
                  <td className="py-2 px-2">Number of columns this item spans</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">rowSpan</td>
                  <td className="py-2 px-2">1 | 2 | 3</td>
                  <td className="py-2 px-2">1</td>
                  <td className="py-2 px-2">Number of rows this item spans</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes to merge with defaults</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Usage Examples */}
        <Section title="Usage Examples">
          <SectionCard
            header={<CardTitle>Code Examples</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`// Basic 4-column grid
<DashboardGrid>
  <DashboardGridItem>
    <BarChart data={...} />
  </DashboardGridItem>
  <DashboardGridItem>
    <DonutChart data={...} />
  </DashboardGridItem>
  {/* ... more items ... */}
</DashboardGrid>

// 3-column with spanning item
<DashboardGrid columns={3} gap="lg">
  <DashboardGridItem span={2}>
    <AreaChart data={...} height={300} />
  </DashboardGridItem>
  <DashboardGridItem>
    <GaugeChart value={75} min={0} max={100} />
  </DashboardGridItem>
  {/* ... more items ... */}
</DashboardGrid>

// Animated grid with stagger effect
<DashboardGrid columns={2} gap="md" animated>
  <DashboardGridItem>
    <Card1 />
  </DashboardGridItem>
  <DashboardGridItem>
    <Card2 />
  </DashboardGridItem>
  {/* ... more items ... */}
</DashboardGrid>

// With row spanning for tall charts
<DashboardGrid columns={4} gap="md">
  <DashboardGridItem span={2} rowSpan={2}>
    <BarChart data={...} height={450} />
  </DashboardGridItem>
  <DashboardGridItem>
    <SmallMetric label="Revenue" value={"$12.5K"} />
  </DashboardGridItem>
  <DashboardGridItem>
    <SmallMetric label="Users" value={"2,340"} />
  </DashboardGridItem>
  {/* ... more items ... */}
</DashboardGrid>`}</code>
            </pre>
          </SectionCard>
        </Section>

        {/* Responsive Behavior */}
        <Section title="Responsive Behavior">
          <SectionCard
            header={<CardTitle>Breakpoint Grid Adaption</CardTitle>}
            contentClassName="p-theme-5"
          >
            <div className="space-y-theme-4">
              <div className="space-y-theme-2">
                <div className="flex items-start gap-theme-3">
                  <Badge>columns=1</Badge>
                  <div>
                    <Text className="text-xs font-semibold">Mobile (all sizes)</Text>
                    <Text className="text-xs text-muted-foreground">
                      Always displays as 1 column on mobile
                    </Text>
                  </div>
                </div>
              </div>

              <div className="space-y-theme-2">
                <div className="flex items-start gap-theme-3">
                  <Badge>columns=2</Badge>
                  <div>
                    <Text className="text-xs font-semibold">Responsive 2-Column</Text>
                    <Text className="text-xs text-muted-foreground">
                      1 column on mobile, 2 columns at md breakpoint and above
                    </Text>
                  </div>
                </div>
              </div>

              <div className="space-y-theme-2">
                <div className="flex items-start gap-theme-3">
                  <Badge>columns=3</Badge>
                  <div>
                    <Text className="text-xs font-semibold">Responsive 3-Column</Text>
                    <Text className="text-xs text-muted-foreground">
                      1 column on mobile, 2 columns at md breakpoint, 3 columns at lg
                      breakpoint and above
                    </Text>
                  </div>
                </div>
              </div>

              <div className="space-y-theme-2">
                <div className="flex items-start gap-theme-3">
                  <Badge>columns=4</Badge>
                  <div>
                    <Text className="text-xs font-semibold">Responsive 4-Column (Default)</Text>
                    <Text className="text-xs text-muted-foreground">
                      1 column on mobile, 2 columns at md breakpoint, 4 columns at lg
                      breakpoint and above
                    </Text>
                  </div>
                </div>
              </div>

              <div className="space-y-theme-2">
                <div className="flex items-start gap-theme-3">
                  <Badge>columns=6</Badge>
                  <div>
                    <Text className="text-xs font-semibold">Responsive 6-Column</Text>
                    <Text className="text-xs text-muted-foreground">
                      2 columns on mobile, 3 columns at md breakpoint, 6 columns at lg
                      breakpoint and above
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </Section>

        {/* Gap Size Reference */}
        <Section title="Gap Size Reference">
          <SectionCard
            header={<CardTitle>CSS Gap Values</CardTitle>}
            contentClassName="p-theme-5"
          >
            <div className="space-y-theme-3 text-sm">
              <div className="grid grid-cols-2 gap-theme-4">
                <div>
                  <Text className="text-xs font-semibold text-muted-foreground mb-1">
                    sm
                  </Text>
                  <Text className="text-xs">0.5rem (8px)</Text>
                </div>
                <div>
                  <Text className="text-xs font-semibold text-muted-foreground mb-1">
                    md
                  </Text>
                  <Text className="text-xs">1rem (16px)</Text>
                </div>
                <div>
                  <Text className="text-xs font-semibold text-muted-foreground mb-1">
                    lg
                  </Text>
                  <Text className="text-xs">1.5rem (24px)</Text>
                </div>
                <div>
                  <Text className="text-xs font-semibold text-muted-foreground mb-1">
                    xl
                  </Text>
                  <Text className="text-xs">2rem (32px)</Text>
                </div>
              </div>
            </div>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsDashboardGridPage as DashboardGridPage };
